import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, Send, CheckCircle2, AlertCircle, Bell, Calendar } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
      ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    }`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {label}
  </span>
);

const InfoRow = ({ label, value, monospace = false }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-t border-neutral-100 first:border-t-0">
    <div className="text-sm text-neutral-500">{label}</div>
    <div
      className={`text-sm text-neutral-900 text-right ${monospace ? "font-mono text-xs" : ""}`}
    >
      {value || "—"}
    </div>
  </div>
);

const AdminEmail = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [weeklySending, setWeeklySending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/status");
      setStatus(data);
      if (!testTo && data.recipients?.[0]) setTestTo(data.recipients[0]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendTest = async () => {
    if (!testTo) return;
    setTestSending(true);
    try {
      await api.post("/admin/email/test", { to: testTo });
      toast.success(`Test-Mail an ${testTo} verschickt`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTestSending(false);
    }
  };

  const sendWeeklyNow = async () => {
    setWeeklySending(true);
    try {
      const { data } = await api.post("/admin/email/send-weekly-now");
      if (data?.sent_to?.length) {
        toast.success(`Wochenreport gesendet an ${data.sent_to.join(", ")}`);
      } else {
        toast.warning("Report nicht gesendet: " + (data?.skipped || "unbekannt"));
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setWeeklySending(false);
    }
  };

  return (
    <AdminShell
      title="E-Mail & Benachrichtigungen"
      subtitle="SMTP-Status, Wochenreport und Sofort-Benachrichtigungen bei neuen Leads"
    >
      {loading || !status ? (
        <div className="rounded-3xl bg-white border border-neutral-200 p-16 grid place-items-center text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* SMTP Status */}
          <div className="lg:col-span-2 rounded-3xl bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-2xl bg-neutral-100 grid place-items-center text-neutral-700">
                  <Mail className="w-4 h-4" />
                </div>
                <h2 className="font-display text-lg font-medium">SMTP Konfiguration</h2>
              </div>
              <StatusBadge
                ok={status.configured}
                label={status.configured ? "Verbunden" : "Nicht konfiguriert"}
              />
            </div>

            <div className="rounded-2xl bg-neutral-50 p-4">
              <InfoRow label="Server" value={status.host} monospace />
              <InfoRow label="Port" value={status.port} monospace />
              <InfoRow label="Verschlüsselung" value={status.use_ssl ? "SSL/TLS" : "STARTTLS"} />
              <InfoRow label="Benutzer" value={status.user} monospace />
              <InfoRow
                label="Absender"
                value={`${status.from_name} <${status.from_email}>`}
                monospace
              />
              <InfoRow
                label="Empfänger Reports"
                value={status.recipients?.join(", ")}
                monospace
              />
              <InfoRow label="Zeitzone" value={status.timezone} />
            </div>

            <p className="mt-4 text-xs text-neutral-500">
              Konfiguration wird aus den Umgebungsvariablen (<code>.env</code>) gelesen.
              Änderungen an Empfängern oder Server-Daten ändern, indem du die <code>.env</code>{" "}
              anpasst und den Backend-Service neu startest.
            </p>
          </div>

          {/* Toggles status */}
          <div className="rounded-3xl bg-white border border-neutral-200 p-6">
            <h2 className="font-display text-lg font-medium mb-4">Aktive Flows</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 shrink-0 rounded-xl bg-sky-50 text-sky-700 grid place-items-center">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">Wochenreport</div>
                  <div className="text-xs text-neutral-500">
                    Jeden Montag · 08:00 · {status.timezone}
                  </div>
                  <StatusBadge
                    ok={status.enable_weekly_report}
                    label={status.enable_weekly_report ? "Aktiv" : "Aus"}
                  />
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                  <Bell className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">Lead-Benachrichtigung</div>
                  <div className="text-xs text-neutral-500">
                    Sofort bei jedem neuen Lead
                  </div>
                  <StatusBadge
                    ok={status.enable_lead_notifications}
                    label={status.enable_lead_notifications ? "Aktiv" : "Aus"}
                  />
                </div>
              </li>
            </ul>
          </div>

          {/* Send test mail */}
          <div className="rounded-3xl bg-white border border-neutral-200 p-6">
            <h2 className="font-display text-lg font-medium">Test-Mail senden</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Bestätige, dass der SMTP-Server eingehende Mails verschickt.
            </p>
            <div className="mt-4 space-y-3">
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="empfaenger@domain.de"
                className="rounded-xl bg-white border-neutral-200 h-11"
                data-testid="email-test-to"
              />
              <button
                onClick={sendTest}
                disabled={testSending || !testTo}
                data-testid="email-test-send"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-950 text-white px-5 py-3 text-sm font-medium hover:bg-black disabled:opacity-60"
              >
                {testSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Test-Mail abschicken
              </button>
            </div>
          </div>

          {/* Send weekly now */}
          <div className="lg:col-span-2 rounded-3xl bg-white border border-neutral-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-lg font-medium">Wochenreport jetzt senden</h2>
                <p className="text-xs text-neutral-500 mt-1">
                  Schickt sofort einen Wochenreport mit den Daten der letzten 7 Tage.
                  Nutze das zum Korrekturlesen vor dem ersten Montagsmail.
                </p>
              </div>
              <button
                onClick={sendWeeklyNow}
                disabled={weeklySending}
                data-testid="email-weekly-send-now"
                className="inline-flex items-center gap-2 rounded-full bg-sky-600 text-white px-5 py-3 text-sm font-medium hover:bg-sky-700 disabled:opacity-60"
              >
                {weeklySending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Wochenreport sofort senden
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-2xl bg-neutral-50 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">
                  Zuletzt versendet
                </div>
                <div className="text-sm font-medium text-neutral-900">
                  {fmtTime(status.last_weekly_sent)}
                </div>
              </div>
              <div className="rounded-2xl bg-neutral-50 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">
                  Letzter Snapshot
                </div>
                <div className="text-sm font-medium text-neutral-900">
                  {status.last_weekly_result?.totals
                    ? `${status.last_weekly_result.totals.views} Views · ${status.last_weekly_result.totals.leads} Leads`
                    : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminEmail;
